/**
 * Manual KYC answer display for questionnaire review.
 * Supports Switch-to-Manual on Hyperverge questions and legacy Manual KYC thread docs.
 */

/** Hyperverge / DigiLocker question IDs that support Switch to Manual. */
export const HYPERVERGE_MANUAL_QUESTION_IDS = new Set([
  "QBM1000002",
  "QBM1000015",
  "QBM1000014",
  "QBM1000043",
]);

/** Legacy document KYC questions (upload + typed values). */
export const MANUAL_KYC_DOCUMENT_QUESTION_IDS = new Set([
  "QBM1000033",
  "QBM1000040",
  "QBM1000024",
  "QBM1000025",
  "QBM1000022",
  "QBM1000023",
  "QBM1000044",
  "QBM1000102",
]);

/** @deprecated Manual KYC predefined thread is retired. */
export const MANUAL_KYC_DEFAULT_THREAD_QUESTION_IDS = [
  "QBM1000033",
  "QBM1000024",
  "QBM1000025",
  "QBM1000022",
  "QBM1000023",
  "QBM1000044",
  "QBM1000102",
];

/** @deprecated Manual KYC predefined thread is retired. */
export const MANUAL_KYC_PREDEFINED_THREAD_NAME = "Manual KYC";

export const isManualKycDocumentQuestion = (questionId) =>
  MANUAL_KYC_DOCUMENT_QUESTION_IDS.has(questionId);

export const isHypervergeManualQuestion = (questionId) =>
  HYPERVERGE_MANUAL_QUESTION_IDS.has(questionId);

/** @deprecated Prefer isManualEntryAnswer / formatManualKycAnswerForDisplay */
export const isManualKycThread = (threadName) =>
  String(threadName || "")
    .trim()
    .toLowerCase() === MANUAL_KYC_PREDEFINED_THREAD_NAME.toLowerCase();

export const parseManualKycAnswerText = (answerText) => {
  if (!answerText || typeof answerText !== "string") return null;
  const trimmed = answerText.trim();
  if (!trimmed.startsWith("{")) return { raw: trimmed };
  try {
    return JSON.parse(trimmed);
  } catch {
    return { raw: trimmed };
  }
};

export const isManualEntryAnswer = (answerText) => {
  const parsed = parseManualKycAnswerText(answerText);
  return Boolean(
    parsed &&
      !parsed.raw &&
      (parsed.entryMode === "MANUAL" ||
        parsed.pan ||
        parsed.gstin ||
        parsed.accNum ||
        parsed.aadhaar ||
        parsed.addressLine1),
  );
};

/**
 * Show typed "Entered values" when answer is Switch-to-Manual JSON,
 * or legacy Manual KYC thread document questions.
 */
export const shouldShowManualKycFields = (questionId, threadName, answerText) => {
  if (answerText && isManualEntryAnswer(answerText)) return true;
  if (isHypervergeManualQuestion(questionId) && isManualEntryAnswer(answerText)) {
    return true;
  }
  return isManualKycDocumentQuestion(questionId) && isManualKycThread(threadName);
};

export const formatManualKycAnswerForDisplay = (answerText) => {
  const parsed = parseManualKycAnswerText(answerText);
  if (!parsed) return null;

  if (parsed.raw) return parsed.raw;

  const lines = [];
  if (parsed.entryMode === "MANUAL") lines.push("Entry mode: Manual");
  if (parsed.pan && !parsed.aadhaar) lines.push(`PAN: ${parsed.pan}`);
  if (parsed.gstin) lines.push(`GSTIN: ${parsed.gstin}`);
  if (parsed.ifsc) lines.push(`IFSC: ${parsed.ifsc}`);
  if (parsed.accNum) lines.push(`Account Number: ${parsed.accNum}`);
  if (parsed.bankName) lines.push(`Bank Name: ${parsed.bankName}`);
  if (parsed.branchName) lines.push(`Branch Name: ${parsed.branchName}`);
  if (parsed.accHolderName)
    lines.push(`Account Holder Name: ${parsed.accHolderName}`);
  if (parsed.directorName) lines.push(`Director Name: ${parsed.directorName}`);
  if (parsed.aadhaar) lines.push(`Aadhaar: ${parsed.aadhaar}`);
  if (parsed.pan && parsed.aadhaar) lines.push(`PAN: ${parsed.pan}`);
  if (parsed.panFileUrl) lines.push(`PAN Document: uploaded`);
  if (parsed.addressLine1) lines.push(`Address 1: ${parsed.addressLine1}`);
  if (parsed.addressLine2) lines.push(`Address 2: ${parsed.addressLine2}`);
  if (parsed.city) lines.push(`City: ${parsed.city}`);
  if (parsed.pinCode) lines.push(`Pincode: ${parsed.pinCode}`);
  if (parsed.state) lines.push(`State: ${parsed.state}`);
  return lines.length ? lines.join("\n") : null;
};
